import { useState } from 'react';
import { X, Sparkles } from 'lucide-react';
import { Button, Input, Alert } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import { useChatDock } from '@/lib/ChatDockContext';
import { slugify } from '@/lib/slug';

/**
 * "New mock API" — name and description only.
 *
 * The `key` is derived server-side (routes/mockApiRouter.js), as is the serve key, and
 * the row is created before the editor opens — so the detail screen only ever edits an
 * environment that exists. Same rationale as NewWorkflowDialog: inventing a unique key
 * by hand meant discovering the clash AFTER authoring the whole thing.
 *
 * Overlay markup follows components/ToolTraceStack.jsx's TraceModal, the app's one
 * working modal, rather than twreact's unused Dialog.
 */
const overlayClass = 'fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4';
const panelClass = 'bg-[var(--app-surface)] rounded-lg shadow-xl w-full max-w-lg flex flex-col';
const textareaClass = 'w-full border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)]';

const errText = (ex) => (ex.response && ex.response.data && ex.response.data.error) || ex.message;

export default function NewMockEnvironmentDialog({ onClose, onCreated }) {
  const { fetchApi } = useAppApi();
  const { openDockWithAgent } = useChatDock() || {};
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [withBuilder, setWithBuilder] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // A preview, not the value: only the server can see which keys are taken, so it may
  // hand back `stripe-sandbox-2`.
  const keyPreview = slugify(name, 'mock');

  const create = async () => {
    setSaving(true);
    setErr('');
    try {
      const created = await fetchApi('mockEnvironmentCreate', {
        name: name.trim(),
        description: description.trim() || null,
      });
      const newId = created?.data?.id ?? created?.id;
      if (!newId) throw new Error('Created the mock, but could not read its new id back from the response.');
      // The dock is mounted in DefaultLayout and survives the navigation, so opening it
      // here means the detail screen loads with Mock API Builder already waiting.
      if (withBuilder && openDockWithAgent) openDockWithAgent('mock_api_builder');
      onCreated(newId);
    } catch (ex) {
      setErr(errText(ex));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={overlayClass} onClick={onClose}>
      <div className={panelClass} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--app-border)]">
          <span className="font-semibold text-sm">New mock API</span>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-[var(--app-border)]/40">
            <X size={16} />
          </button>
        </div>

        <div className="px-4 py-4 space-y-3">
          {err && <Alert variant="error" title="Could not create">{err}</Alert>}

          <Input
            label="Name"
            value={name}
            autoFocus
            placeholder="e.g. Stripe sandbox"
            onChange={(e) => setName(e.target.value)}
          />

          <label className="block space-y-1">
            <span className="text-sm">Description</span>
            <textarea
              className={textareaClass}
              rows={3}
              value={description}
              placeholder="Which real API this stands in for, and why you are mocking it."
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>

          <div className="text-xs text-secondary-500">
            Key: <code className="font-mono">{name.trim() ? keyPreview : '—'}</code>
            <span className="ml-1">— generated automatically. The public URL is generated too, and is unguessable.</span>
          </div>

          {openDockWithAgent && (
            <label className="flex items-start gap-2 text-sm rounded-md border border-[var(--app-border)] p-2">
              <input
                type="checkbox"
                className="mt-1"
                checked={withBuilder}
                onChange={(e) => setWithBuilder(e.target.checked)}
              />
              <span>
                <span className="flex items-center gap-1 font-medium">
                  <Sparkles size={13} /> Draft it with Mock API Builder
                </span>
                <span className="block text-xs text-secondary-500">
                  Opens the assistant on the detail screen. Describe the API above — or paste a sample
                  response — and ask it to draft the routes. Nothing it proposes is saved until you
                  review and Save.
                </span>
              </span>
            </label>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[var(--app-border)]">
          <Button size="sm" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={create} disabled={saving || !name.trim()}>
            {saving ? 'Creating…' : 'Create mock API'}
          </Button>
        </div>
      </div>
    </div>
  );
}
